// src/lib/supabase.ts
import { createClient } from '@supabase/supabase-js';
import { logError } from './errorHandler';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ Missing environment variables:', {
    hasUrl: !!supabaseUrl,
    hasKey: !!supabaseAnonKey
  });
  throw new Error('Missing Supabase environment variables');
}

console.log('🔌 Initializing Supabase client with URL:', supabaseUrl);

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    storage: localStorage,
    detectSessionInUrl: true,
    flowType: 'pkce'
  }
});

// Handle auth state changes
supabase.auth.onAuthStateChange(async (event, session) => {
  try {
    console.log('🔄 Auth state changed:', event);

    if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
      console.log('🚪 User signed out or deleted, clearing storage');
      localStorage.clear();
      window.location.href = '/';
      return;
    }

    if (!session?.user?.id) {
      console.log('ℹ️ No user in session');
      return;
    }

    if (event === 'SIGNED_IN') {
      console.log('✅ User signed in — session and profile will be checked at page level');
    }
  } catch (error) {
    logError(error, { context: 'Auth state change handler' });
    await supabase.auth.signOut();
    localStorage.clear();
    window.location.href = '/';
  }
});

// Session check used throughout the app
export async function checkSessionValidity() {
  try {
    console.log('🔍 Checking session validity');
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) throw error;

    const isValid = !!session?.user?.id;
    console.log(isValid ? '✅ Session valid' : '⚠️ Session invalid');
    return isValid;
  } catch (error) {
    logError(error, { context: 'Session validity check' });
    localStorage.clear();
    window.location.href = '/';
    return false;
  }
}

// Safe test connection with retry logic
export const testConnection = async (retries = 3, delay = 1000) => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (!session?.user?.id) {
    console.warn('⚠️ Skipping testConnection — no valid session');
    return;
  }

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(`🔍 Testing database connection (attempt ${attempt}/${retries})...`);

      const { count, error: healthError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      if (healthError) throw healthError;

      console.log(`✅ Database connection successful. Profile count: ${count}`);
      return;
    } catch (error) {
      logError(error, { 
        context: 'Database connection test',
        attempt,
        maxRetries: retries
      });

      if (attempt === retries) {
        console.error('❌ All connection attempts failed - Please check your Supabase configuration');
        return;
      }

      console.log(`⏳ Retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// Initialize session from URL tokens if present
const initializeSession = async () => {
  try {
    console.log('🔄 Initializing session');
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) {
      logError(sessionError, { context: 'Session initialization' });
      throw sessionError;
    }

    if (session?.user?.id) {
      console.log('✅ Session found:', session.user.id);
      return;
    }

    const params = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');

    if (accessToken && refreshToken) {
      console.log('🔑 Found tokens in URL, setting session');
      await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
      console.log('✅ Session set from URL tokens');
    } else {
      console.log('ℹ️ No tokens found in URL');
    }
  } catch (error) {
    logError(error, { context: 'Session initialization' });
  }
};

// Run startup tasks
initializeSession().then(async () => {
  const isValid = await checkSessionValidity();
  if (isValid) {
    await testConnection();
  }
});